import { VACANCY_LIST, type VacancyListKind } from "./constants";
import { findOverride, isInSegment } from "./overrides";

import type {
  SegmentVacanciesResponse,
  SegmentVacancyItem,
  VacancyOverride,
} from "@/features/segments/types";

export type VacancyPage = SegmentVacanciesResponse & {
  list: VacancyListKind;
  fetchedFor: VacancyOverride[];
};

export type VacancyPageParam = { page: number; overrides?: VacancyOverride[] };

const belongsTo = (
  item: SegmentVacancyItem,
  overrides: VacancyOverride[],
  list: VacancyListKind,
) =>
  isInSegment(
    findOverride(overrides, item.vacancy.vacancyId),
    item.matchesRules,
  ) ===
  (list === VACANCY_LIST.INCLUDED);

export const itemsInList = (
  pages: VacancyPage[],
  overrides: VacancyOverride[],
  list: VacancyListKind,
) => {
  const seen = new Set<number>();
  return pages
    .flatMap((page) => page.items)
    .filter((item) => {
      if (seen.has(item.vacancy.vacancyId)) return false;
      seen.add(item.vacancy.vacancyId);
      return belongsTo(item, overrides, list);
    });
};

const loadedIds = (pages: VacancyPage[]) =>
  new Set(
    pages.flatMap((page) => page.items.map((item) => item.vacancy.vacancyId)),
  );

export const listTotal = (
  pages: VacancyPage[],
  overrides: VacancyOverride[],
  list: VacancyListKind,
) => {
  const firstPage = pages[0];
  if (!firstPage) return undefined;

  const loaded = loadedIds(pages);
  const flippedOut = loaded.size - itemsInList(pages, overrides, list).length;
  return firstPage.totalCount - flippedOut;
};

// Later pages are fetched with the overrides of the first page, so the server keeps the same row order
// and flipping a loaded row cannot shift an unseen row across a page boundary.
export const getNextPageParam = (
  pages: VacancyPage[],
): VacancyPageParam | undefined => {
  const lastPage = pages[pages.length - 1];
  if (!lastPage || lastPage.page * lastPage.pageSize >= lastPage.totalCount)
    return undefined;
  return { page: lastPage.page + 1, overrides: pages[0].fetchedFor };
};

const changedVacancyIds = (
  before: VacancyOverride[],
  after: VacancyOverride[],
) => {
  const ids = new Set(
    [...before, ...after].map((override) => override.vacancyId),
  );
  return [...ids].filter(
    (vacancyId) =>
      findOverride(before, vacancyId) !== findOverride(after, vacancyId),
  );
};

export const needsReset = (
  pages: VacancyPage[],
  overrides: VacancyOverride[],
) => {
  const firstPage = pages[0];
  if (!firstPage) return false;

  const loaded = loadedIds(pages);
  return changedVacancyIds(firstPage.fetchedFor, overrides).some(
    (vacancyId) => !loaded.has(vacancyId),
  );
};
